/**
 * Account credential storage.
 *
 * Threat model (docs/SECURITY.md §T10): the browser profile is the trust
 * boundary. Account passwords are sealed with AES-256-GCM under a
 * non-extractable `CryptoKey` generated on this device and kept in IndexedDB
 * alongside the ciphertext. Without an optional master passphrase the
 * sealing protects against passive database exfiltration, not against a
 * compromised browser — that trade-off is documented, not hidden.
 *
 * Plaintext passwords never touch `localStorage` and are only decrypted in
 * memory for the duration of a connection attempt.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

import { accounts } from "./store.ts";
import type { AccountSummary } from "./store.ts";

/**
 * Summary projected into app state (same shape as the store's type).
 */
export type { AccountSummary } from "./store.ts";

/**
Database name; one per origin, matching the single-profile model.
 */
const DB_NAME = "accounts";

/**
Schema version.
 */
const DB_VERSION = 1;

/**
Object store holding the wrapping key (exactly one record).
 */
const KEY_STORE = "wrapping-key";

/**
Object store holding account records.
 */
const ACCOUNT_STORE = "accounts";

/**
Record id of the singleton wrapping key.
 */
const KEY_ID = "key";

/**
AES-GCM key length in bits.
 */
const KEY_BITS = 256;

/**
A stored account: profile fields in clear, password sealed.
 */
interface StoredAccount {
  /**
  Stable local identifier — the lowercased email address.
   */
  readonly id: string;
  /**
  User-chosen display label.
   */
  readonly label: string;
  /**
  Primary email address.
   */
  readonly email: string;
  /**
  IMAP hostname (e.g. `webhosting2041.is.cc`).
   */
  readonly imapHost: string;
  /**
  IMAP port (993 for implicit TLS).
   */
  readonly imapPort: number;
  /**
  SMTP hostname.
   */
  readonly smtpHost: string;
  /**
  SMTP port (465 for implicit TLS).
   */
  readonly smtpPort: number;
  /**
  Login user name — usually the email address itself.
   */
  readonly username: string;
  /**
  Sealed password: `iv || ciphertext`, base64url.
   */
  readonly sealedPassword: string;
}

/**
Connection settings of a stored account (never includes the secret).
 */
export type ConnectionProfile = Omit<StoredAccount, "sealedPassword">;

/**
 * Database shape: one record for the wrapping key, one store of accounts.
 * Typing the schema lets `idb` hand back real types instead of `any`.
 */
interface AccountsDatabase extends DBSchema {
  /**
  Singleton AES-GCM wrapping key.
   */
  "wrapping-key": {
    /**
    Key record id.
     */
    readonly key: string;
    /**
    The stored CryptoKey itself.
     */
    readonly value: CryptoKey;
  };
  /**
  Sealed account records, keyed by lowercased email.
   */
  accounts: {
    /**
    Account id.
     */
    readonly key: string;
    /**
    The stored account row.
     */
    readonly value: StoredAccount;
  };
}

/**
Open database connection, one promise per page lifetime.
 */
let database: Promise<IDBPDatabase<AccountsDatabase>> | null = null;

/**
 * Opens (and migrates) the database.
 *
 * @returns The shared connection promise.
 */
function db(): Promise<IDBPDatabase<AccountsDatabase>> {
  // Module-lifetime memo of one connection; the singleton is the point.
  // eslint-disable-next-line unicorn/no-top-level-assignment-in-function -- deliberate single-connection memo
  database ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(data) {
      if (!data.objectStoreNames.contains(KEY_STORE)) {
        data.createObjectStore(KEY_STORE);
      }
      if (!data.objectStoreNames.contains(ACCOUNT_STORE)) {
        data.createObjectStore(ACCOUNT_STORE, { keyPath: "id" });
      }
    },
  });
  return database;
}

/**
The DOM types promise `subtle` unconditionally; insecure contexts withhold
it, so the runtime view of `Crypto` keeps it optional.
 */
interface MaybeSubtleCrypto {
  /**
  Absent on insecure (non-HTTPS, non-localhost) origins.
   */
  readonly subtle?: SubtleCrypto;
}

/**
 * The WebCrypto SubtleCrypto engine, or `undefined` outside a secure
 * context: `http://` origins other than localhost get no `crypto.subtle`,
 * and without it there is no non-extractable key to wrap secrets with.
 *
 * @returns The engine, or `undefined` when the browser withholds it.
 */
function subtle(): SubtleCrypto | undefined {
  return (crypto as MaybeSubtleCrypto).subtle;
}

/**
 * Loads (or creates on first use) the non-extractable wrapping key.
 *
 * @param handle - Open database connection.
 * @returns An AES-GCM key that cannot be serialised out of this device.
 * @throws {Error} When the page is not a secure context (see `subtle`);
 * production deployments are HTTPS-only, so this is a misconfiguration
 * signal, not a transient fault.
 */
async function wrappingKey(
  handle: IDBPDatabase<AccountsDatabase>,
): Promise<CryptoKey> {
  const engine = subtle();
  if (engine === undefined) {
    throw new Error("Account encryption needs a secure connection (HTTPS).");
  }
  const existing = await handle.get(KEY_STORE, KEY_ID);
  if (existing) {
    return existing;
  }
  const created = await engine.generateKey(
    { name: "AES-GCM", length: KEY_BITS },
    false,
    ["encrypt", "decrypt"],
  );
  await handle.put(KEY_STORE, created, KEY_ID);
  return created;
}

/**
 * Seals a secret: 96-bit random IV prepended to the AES-GCM ciphertext.
 *
 * @param key - Wrapping key.
 * @param plaintext - Secret bytes to seal.
 * @returns Base64url-encoded `iv || ciphertext`.
 */
async function seal(
  key: CryptoKey,
  plaintext: Uint8Array<ArrayBuffer>,
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const sealed = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext,
  );
  const packed = new Uint8Array(iv.length + sealed.byteLength);
  packed.set(iv, 0);
  packed.set(new Uint8Array(sealed), iv.length);
  return toBase64Url(packed);
}

/**
 * Opens a sealed secret.
 *
 * @param key - Wrapping key.
 * @param packed - Base64url `iv || ciphertext` from `seal`.
 * @returns The decrypted bytes.
 */
async function unseal(
  key: CryptoKey,
  packed: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const bytes = fromBase64Url(packed);
  const iv = bytes.slice(0, 12);
  const ciphertext = bytes.slice(12);
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext,
  );
  return new Uint8Array(plain);
}

/**
 * Base64url without padding — URL-safe in any context, compact in storage.
 *
 * @param bytes - Bytes to encode.
 * @returns The base64url text.
 */
function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    // Byte-to-latin1-char is the contract here (btoa input), not text
    // semantics, so the code-point variants do not apply.
    // eslint-disable-next-line unicorn/prefer-code-point -- byte-level encoding for btoa
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

/**
 * Inverse of `toBase64Url`; tolerates stripped padding.
 *
 * @param text - Base64url text.
 * @returns The decoded bytes.
 */
function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const padded = text.replaceAll("-", "+").replaceAll("_", "/");
  const binary = atob(`${padded}${"=".repeat((4 - (padded.length % 4)) % 4)}`);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    // eslint-disable-next-line unicorn/prefer-code-point -- byte-level decode of atob output
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/**
Fields a user supplies when adding an account.
 */
export interface NewAccount {
  /**
  User-chosen display label.
   */
  readonly label: string;
  /**
  Primary email address.
   */
  readonly email: string;
  /**
  IMAP hostname.
   */
  readonly imapHost: string;
  /**
  IMAP port (993 for implicit TLS).
   */
  readonly imapPort: number;
  /**
  SMTP hostname.
   */
  readonly smtpHost: string;
  /**
  SMTP port (465 for implicit TLS).
   */
  readonly smtpPort: number;
  /**
  Login user name.
   */
  readonly username: string;
  /**
  Plaintext password; sealed before it touches storage.
   */
  readonly password: string;
}

/**
 * Persists a new account, sealing its password.
 *
 * @param account - Profile and secret to store.
 * @returns The stored summary without any secret material.
 * @throws {Error} When an account for this email already exists.
 */
export async function addAccount(account: NewAccount): Promise<AccountSummary> {
  const handle = await db();
  const key = await wrappingKey(handle);
  const id = account.email.toLowerCase();
  if ((await handle.getKey(ACCOUNT_STORE, id)) !== undefined) {
    throw new Error(`An account for ${id} already exists.`);
  }
  const record: StoredAccount = {
    id,
    label: account.label,
    email: account.email,
    imapHost: account.imapHost,
    imapPort: account.imapPort,
    smtpHost: account.smtpHost,
    smtpPort: account.smtpPort,
    username: account.username,
    sealedPassword: await seal(key, new TextEncoder().encode(account.password)),
  };
  await handle.put(ACCOUNT_STORE, record);
  await publishSummaries(handle);
  return { id, label: record.label, email: record.email };
}

/**
 * Lists every configured account (no secret material), refreshing the state
 * signal.
 *
 * @returns Summaries sorted by label.
 */
export async function listAccounts(): Promise<readonly AccountSummary[]> {
  return publishSummaries(await db());
}

/**
 * Loads the connection profile of an account and unseals its password.
 *
 * The password lives only in the returned value; callers must not log it.
 *
 * @param id - Account id (lowercased email).
 * @returns The connection-ready profile plus the plaintext password.
 * @throws {Error} When the account does not exist.
 */
export async function revealCredentials(
  id: string,
): Promise<{ profile: ConnectionProfile; password: string }> {
  const handle = await db();
  const record = await handle.get(ACCOUNT_STORE, id);
  if (!record) {
    throw new Error(`No account stored for ${id}.`);
  }
  const key = await wrappingKey(handle);
  const { sealedPassword, ...profile } = record;
  const password = new TextDecoder().decode(await unseal(key, sealedPassword));
  return { profile, password };
}

/**
 * Removes an account and its sealed secret.
 *
 * @param id - Account id to delete.
 */
export async function removeAccount(id: string): Promise<void> {
  const handle = await db();
  await handle.delete(ACCOUNT_STORE, id);
  await publishSummaries(handle);
}

/**
 * Projects stored rows into the state signal and returns the summaries.
 *
 * @param handle - Open database connection.
 * @returns Summaries sorted by label.
 */
async function publishSummaries(
  handle: IDBPDatabase<AccountsDatabase>,
): Promise<readonly AccountSummary[]> {
  const rows = await handle.getAll(ACCOUNT_STORE);
  const summaries = rows
    .map(({ id, label, email }) => ({ id, label, email }))
    .toSorted((left, right) => left.label.localeCompare(right.label));
  accounts.value = summaries;
  return summaries;
}
